import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  getAllQuotes,
  getAdminStats,
  bulkArchiveQuotes,
  bulkUnarchiveQuotes,
  getAdminInvoices,
  openInvoicePdf,
  printInvoicePdf,
  sendInvoiceEmail,
  getInvoiceShareLink,
  getOwnerStats
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useLanguage } from "@/lib/LanguageContext";
import { toast } from "sonner";
import GoldShieldLogo from "@/components/GoldShieldLogo";
import { 
  Search,
  Eye,
  RefreshCw,
  Package,
  FileText,
  Clock,
  CreditCard,
  CheckCircle2,
  LayoutDashboard,
  ArrowUpRight,
  Archive,
  ArchiveRestore,
  AlertTriangle,
  Loader2,
  Share2,
  Copy,
  Check,
  FileDown,
  LogOut,
  User,
  Users,
  Printer,
  Mail,
  PoundSterling,
  X,
  Shield,
  BookOpen,
  Globe,
  UserCheck,
  Inbox,
  ShoppingCart,
  Newspaper,
  Menu
} from "lucide-react";
import { cn } from "@/lib/utils";

const statusColors = {
  new: "badge-new",
  quoted: "badge-quoted", 
  paid: "badge-paid",
  collected: "badge-collected",
  delivered: "badge-delivered"
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  
  // Dynamic status labels based on language
  const statusLabels = {
    new: t("statusLabels.new"),
    quoted: t("statusLabels.quoted"),
    paid: t("statusLabels.paid"),
    collected: t("statusLabels.collected"),
    delivered: t("statusLabels.delivered")
  };
  
  // Ref for table to scroll to
  const tableRef = useRef(null);
  
  // Mobile sidebar state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("active"); // "active" or "archived"
  
  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareInvoice, setShareInvoice] = useState(null);
  const [shareLink, setShareLink] = useState("");
  const [sharing, setSharing] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  
  // Owner stats state
  const [ownerStats, setOwnerStats] = useState({
    total_visits: 0,
    unique_visitors: 0,
    requests_received: 0,
    paid_orders: 0
  });
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Modal states
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Share state
  const [linkCopied, setLinkCopied] = useState(false);

  // Check if user is OWNER
  const isOwner = admin?.role === "owner" || admin?.role === "admin";

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      toast.success(t("toasts.logoutSuccess"));
      // Small delay to ensure state is cleared before navigation
      setTimeout(() => {
        window.location.href = "/admin/login";
      }, 100);
    } catch (error) {
      console.error("Logout error:", error);
      toast.error(t("toasts.logoutError"));
    }
  };

  // Format GBP currency
  const formatGBP = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const buildAddressLines = (address, fallbackPostcode) => {
    if (!address && !fallbackPostcode) {
      return [];
    }
    const lines = [];
    if (address?.company_name) {
      lines.push(address.company_name);
    }
    const contactLine = [address?.contact_name, address?.contact_phone].filter(Boolean).join(" — ");
    if (contactLine) {
      lines.push(contactLine);
    }
    if (address?.address_line_1) {
      lines.push(address.address_line_1);
    }
    if (address?.address_line_2) {
      lines.push(address.address_line_2);
    }
    const cityLine = [address?.city, address?.county].filter(Boolean).join(", ");
    if (cityLine) {
      lines.push(cityLine);
    }
    if (address?.postcode) {
      lines.push(address.postcode);
    }
    if (address?.country) {
      lines.push(address.country);
    }
    if (!lines.length && fallbackPostcode) {
      lines.push(fallbackPostcode);
    }
    return lines;
  };

  const formatPaidDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    return isNaN(date.getTime()) ? "—" : date.toLocaleString('ro-RO');
  };

  // Handle card click - quick filter
  const handleCardClick = (status) => {
    if (filter === status) {
      // If clicking the same filter, clear it (toggle behavior)
      setFilter("all");
    } else {
      setFilter(status);
    }
    
    // Scroll to table after a short delay for state update
    setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Clear filter handler
  const clearFilter = () => {
    setFilter("all");
  };

  // Share RTS Express function
  const handleShareRTS = async () => {
    const baseUrl = window.location.origin;
    const shareData = {
      title: 'RTS EXPRESS LTD',
      text: 'RTS EXPRESS LTD – Same-day delivery. When it matters.\nGet a quote and pay online here:',
      url: baseUrl
    };

    // Use Web Share API when available
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success(t("toasts.linkShared"));
        return;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share error:', error);
        }
      }
    }

    // Fallback: copy to clipboard
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(baseUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = baseUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setLinkCopied(true);
      toast.success(t("toasts.linkCopied"));
      setTimeout(() => setLinkCopied(false), 3000);
    } catch (error) {
      console.error('Copy error:', error);
      window.prompt("Copy link manually:", baseUrl);
    }
  };

  // Share Staff Login Link function
  const [staffLinkCopied, setStaffLinkCopied] = useState(false);
  
  const handleShareStaffLink = async () => {
    // Use official production URL for staff login link
    const adminLoginUrl = 'https://rtsexpress.co.uk/admin';
    const shareData = {
      title: 'RTS Admin Login',
      text: language === 'ro' ? 'Accesează panoul de administrare RTS Express:' : 'Access RTS Express admin panel:',
      url: adminLoginUrl
    };

    // Use Web Share API when available
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success(t("toasts.staffLinkShared"));
        return;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share error:', error);
        }
      }
    }

    // Fallback: copy to clipboard
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(adminLoginUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = adminLoginUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setStaffLinkCopied(true);
      toast.success(t("toasts.staffLinkCopied"));
      setTimeout(() => setStaffLinkCopied(false), 3000);
    } catch (error) {
      console.error('Copy error:', error);
      window.prompt("Copy link manually:", adminLoginUrl);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const isArchived = viewMode === "archived";
      const [quotesData, statsData] = await Promise.all([
        getAllQuotes(filter === "all" ? null : filter, isArchived),
        getAdminStats()
      ]);
      setQuotes(Array.isArray(quotesData) ? quotesData : []);
      setStats(statsData && typeof statsData === 'object' ? statsData : null);
      // Reset selection when data changes
      setSelectedIds(new Set());
      
      // Fetch owner stats if OWNER
      if (isOwner) {
        try {
          const ownerData = await getOwnerStats();
          setOwnerStats(ownerData);
        } catch (err) {
          console.error("Error fetching owner stats:", err);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t("toasts.dataLoadError"));
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const invoicesData = await getAdminInvoices("paid");
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error(t("toasts.invoiceLoadError"));
    } finally {
      setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    if (viewMode === "invoices") {
      fetchInvoices();
      return;
    }
    fetchData();
  }, [filter, viewMode]);

  // Reset selection when filter/search changes
  useEffect(() => {
    if (viewMode !== "invoices") {
      setSelectedIds(new Set());
    }
  }, [filter, search, viewMode]);

  const handlePrintInvoice = async (invoiceId) => {
    try {
      await printInvoicePdf(invoiceId);
    } catch (error) {
      console.error('Print error:', error);
      toast.error(t("toasts.invoicePrintError"));
    }
  };

  const handleShareInvoice = async (invoice) => {
    setSharing(true);
    try {
      const shareResponse = await getInvoiceShareLink(invoice.id);
      const shareUrl = `${window.location.origin}${shareResponse.share_url}`;
      const shareData = {
        title: `${language === 'ro' ? 'Factura' : 'Invoice'} RTS EXPRESS - ${invoice.invoice_number}`,
        text: `${language === 'ro' ? 'Factura pentru' : 'Invoice for'} ${invoice.company_name || 'client'}`,
        url: shareUrl
      };

      if (navigator.share) {
        try {
          await navigator.share(shareData);
          toast.success(t("toasts.linkShared"));
          return;
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Share error:', error);
          }
        }
      }

      setShareInvoice(invoice);
      setShareLink(shareUrl);
      setShareModalOpen(true);
    } catch (error) {
      console.error('Share error:', error);
      toast.error(t("toasts.shareError"));
    } finally {
      setSharing(false);
    }
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success(t("toasts.linkCopiedSuccess"));
    } catch (error) {
      console.error('Copy error:', error);
      toast.error(t("toasts.copyError"));
    }
  };

  const handleSendInvoiceEmail = async () => {
    if (!shareInvoice) return;
    setSendingInvoice(true);
    try {
      await sendInvoiceEmail(shareInvoice.id);
      toast.success(t("toasts.invoiceSentSuccess"));
    } catch (error) {
      console.error('Send email error:', error);
      toast.error(t("toasts.emailSendError"));
    } finally {
      setSendingInvoice(false);
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const company = (quote.company_name || "").toString().toLowerCase();
    const contact = (quote.contact_name || "").toString().toLowerCase();
    const pickup = (quote.pickup_postcode || "").toString().toLowerCase();
    const delivery = (quote.delivery_postcode || "").toString().toLowerCase();
    const id = (quote.id || "").toString().toLowerCase();
    return (
      company.includes(searchLower) ||
      contact.includes(searchLower) ||
      pickup.includes(searchLower) ||
      delivery.includes(searchLower) ||
      id.includes(searchLower)
    );
  });

  // Selection helpers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredQuotes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredQuotes.map(q => q.id)));
    }
  };

  const toggleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected = filteredQuotes.length > 0 && selectedIds.size === filteredQuotes.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < filteredQuotes.length;

  // Check if selection contains paid quotes (for archive warning - only for STAFF)
  const selectedQuotes = filteredQuotes.filter(q => selectedIds.has(q.id));
  const hasPaidInSelection = selectedQuotes.some(q => q.status === "paid");
  // OWNER can archive all requests including paid, STAFF cannot archive paid
  const eligibleForArchive = isOwner 
    ? selectedQuotes 
    : selectedQuotes.filter(q => q.status !== "paid");

  // Bulk archive handler
  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return;
    
    setBulkLoading(true);
    try {
      const result = await bulkArchiveQuotes(Array.from(selectedIds), archiveReason || null);
      
      // Build toast message
      let message = language === 'ro' 
        ? `Arhivate ${result.archived_count} cereri.`
        : `Archived ${result.archived_count} requests.`;
      if (result.blocked_paid_ids.length > 0) {
        message += language === 'ro'
          ? ` Sărite ${result.blocked_paid_ids.length} plătite (audit).`
          : ` Skipped ${result.blocked_paid_ids.length} paid (audit).`;
      }
      if (result.skipped_already_archived_ids.length > 0) {
        message += language === 'ro'
          ? ` Sărite ${result.skipped_already_archived_ids.length} deja arhivate.`
          : ` Skipped ${result.skipped_already_archived_ids.length} already archived.`;
      }
      
      toast.success(message);
      setShowArchiveModal(false);
      setArchiveReason("");
      fetchData();
    } catch (error) {
      console.error("Bulk archive error:", error);
      toast.error(t("toasts.archiveError"));
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk unarchive handler
  const handleBulkUnarchive = async () => {
    if (selectedIds.size === 0) return;
    
    setBulkLoading(true);
    try {
      const result = await bulkUnarchiveQuotes(Array.from(selectedIds));
      
      let message = language === 'ro'
        ? `Dezarhivate ${result.unarchived_count} cereri.`
        : `Unarchived ${result.unarchived_count} requests.`;
      if (result.skipped_active_ids.length > 0) {
        message += language === 'ro'
          ? ` Sărite ${result.skipped_active_ids.length} deja active.`
          : ` Skipped ${result.skipped_active_ids.length} already active.`;
      }
      
      toast.success(message);
      setShowUnarchiveModal(false);
      fetchData();
    } catch (error) {
      console.error("Bulk unarchive error:", error);
      toast.error(t("toasts.unarchiveError"));
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] lg:flex overflow-x-hidden">
      {/* Mobile Header with Menu Button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0f172a] border-b border-slate-800 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <GoldShieldLogo className="w-8 h-10" />
          <div>
            <span className="font-['Barlow_Condensed'] text-base font-bold text-white block leading-tight">
              RTS EXPRESS
            </span>
            <span className="text-[8px] text-slate-500">SAME DAY COURIER</span>
          </div>
        </Link>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-slate-400 hover:text-white transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: fixed, Mobile: drawer */}
      <aside className={cn(
        "bg-[#0f172a] border-r border-slate-800 flex flex-col transition-transform duration-300 z-50",
        // Desktop styles
        "lg:w-64 lg:fixed lg:h-full lg:translate-x-0",
        // Mobile styles
        "fixed h-full w-72 top-0 left-0",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        <div className="p-6 flex-1 overflow-y-auto pt-16 lg:pt-6">
          {/* Logo - Hidden on mobile (shown in mobile header) */}
          <Link to="/" className="hidden lg:flex items-center gap-3 mb-8">
            <GoldShieldLogo className="w-10 h-12" />
            <div>
              <span className="font-['Barlow_Condensed'] text-lg font-bold text-white block leading-tight">
                RTS EXPRESS
              </span>
              <span className="text-[9px] text-slate-500">SAME DAY COURIER</span>
            </div>
          </Link>

          <nav className="space-y-2">
            <button 
              onClick={() => { setViewMode("active"); setMobileMenuOpen(false); }}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-sm w-full text-left transition-colors",
                viewMode === "active" 
                  ? "bg-slate-800 text-white" 
                  : "text-slate-400 hover:bg-slate-800/50 hover:text-white"
              )}
              data-testid="nav-active-requests-btn"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>{t("sidebar.activeRequests")}</span>
            </button>
            <button 
              onClick={() => { setViewMode("archived"); setMobileMenuOpen(false); }}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-sm w-full text-left transition-colors",
                viewMode === "archived" 
                  ? "bg-slate-800 text-white" 
                  : "text-slate-400 hover:bg-slate-800/50 hover:text-white"
              )}
              data-testid="nav-archived-requests-btn"
            >
              <Archive className="w-5 h-5" />
              <span>{t("sidebar.archived")}</span>
            </button>
            <button 
              onClick={() => { setViewMode("invoices"); setMobileMenuOpen(false); }}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-sm w-full text-left transition-colors",
                viewMode === "invoices" 
                  ? "bg-slate-800 text-white" 
                  : "text-slate-400 hover:bg-slate-800/50 hover:text-white"
              )}
              data-testid="nav-invoices-btn"
            >
              <FileText className="w-5 h-5" />
              <span>{t("sidebar.paidInvoices")}</span>
            </button>
            <Link 
              to="/" 
              className="flex items-center gap-3 px-4 py-3 rounded-sm text-slate-400 hover:bg-slate-800/50 hover:text-white transition-colors"
              data-testid="nav-client-site-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <ArrowUpRight className="w-5 h-5" />
              <span>{t("sidebar.clientSite")}</span>
            </Link>
            
            {/* User Management - Only visible for OWNER */}
            {(admin?.role === "owner" || admin?.role === "admin") && (
              <Link 
                to="/admin/users" 
                className="flex items-center gap-3 px-4 py-3 rounded-sm text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 transition-colors mt-2"
                data-testid="nav-user-management-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Users className="w-5 h-5" />
                <span>{t("sidebar.userManagement")}</span>
              </Link>
            )}
            
            {/* Activity Logs - OWNER only */}
            {(admin?.role === "owner" || admin?.role === "admin") && (
              <Link
                to="/admin/activity-logs"
                className="flex items-center gap-3 px-4 py-3 rounded-sm text-slate-400 hover:bg-slate-800/50 hover:text-white transition-colors mt-2"
                data-testid="nav-activity-logs-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                <FileText className="w-5 h-5" />
                <span>{t("sidebar.activityLogs")}</span>
              </Link>
            )}
            
            {/* Security Settings */}
            <Link
              to="/admin/security"
              className="flex items-center gap-3 px-4 py-3 rounded-sm text-slate-400 hover:bg-slate-800/50 hover:text-white transition-colors mt-2"
              data-testid="nav-security-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Shield className="w-5 h-5" />
              <span>{t("sidebar.security2fa")}</span>
            </Link>
            
            {/* Documentation - OWNER only */}
            {(admin?.role === "owner" || admin?.role === "admin") && (
              <Link
                to="/admin/documentation"
                className="flex items-center gap-3 px-4 py-3 rounded-sm text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 transition-colors mt-2"
                data-testid="nav-documentation-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                <BookOpen className="w-5 h-5" />
                <span>{t("sidebar.documentation")}</span>
              </Link>
            )}
            
            {/* Blog Management - OWNER only */}
            {(admin?.role === "owner" || admin?.role === "admin") && (
              <Link
                to="/admin/blog"
                className="flex items-center gap-3 px-4 py-3 rounded-sm text-cyan-400 hover:bg-cyan-500/10 hover:text-cyan-300 transition-colors mt-2"
                data-testid="nav-blog-management-link"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Newspaper className="w-5 h-5" />
                <span>Blog Management</span>
              </Link>
            )}
            
            {/* Sign Out Button - in visible area */}
            <button 
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-sm w-full text-left transition-colors text-red-400 hover:bg-red-500/10 hover:text-red-300 mt-2"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span>{t("sidebar.signOut")}</span>
            </button>
            
            {/* Share RTS Express Button */}
            <button 
              onClick={handleShareRTS}
              className="flex items-center gap-3 px-4 py-3 rounded-sm w-full text-left transition-colors bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20 mt-4"
              data-testid="share-rts-btn"
            >
              {linkCopied ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>{t("sidebar.linkCopied")}</span>
                </>
              ) : (
                <>
                  <Share2 className="w-5 h-5" />
                  <span>{t("sidebar.shareRts")}</span>
                </>
              )}
            </button>
            
            {/* Link Staff Button - Share admin login link */}
            <button 
              onClick={handleShareStaffLink}
              className="flex items-center gap-3 px-4 py-3 rounded-sm w-full text-left transition-colors bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 mt-2"
              data-testid="share-staff-link-btn"
            >
              {staffLinkCopied ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>{t("sidebar.linkCopied")}</span>
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  <span>{t("sidebar.linkStaff")}</span>
                </>
              )}
            </button>
            
            {/* Language Switcher Button */}
            <button 
              onClick={toggleLanguage}
              className="flex items-center gap-3 px-4 py-3 rounded-sm w-full text-left transition-colors bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 mt-2"
              data-testid="language-toggle-btn"
            >
              <Globe className="w-5 h-5" />
              <span>{language === 'ro' ? '🇬🇧 English' : '🇷🇴 Română'}</span>
            </button>
          </nav>

        {/* Quick Stats in Sidebar - CLICKABLE */}
        {stats && (
          <div className="px-6 mt-8">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-4">{t("sidebarStats.title")}</p>
            <div className="space-y-2">
              {/* Total - Click to show ALL */}
              <button
                onClick={() => setFilter("all")}
                className={cn(
                  "flex justify-between items-center w-full px-3 py-2 rounded-sm transition-all",
                  filter === "all" 
                    ? "bg-slate-700/50 ring-1 ring-slate-500" 
                    : "hover:bg-slate-800/50"
                )}
                data-testid="sidebar-filter-all"
              >
                <span className="text-slate-400 text-sm">{t("sidebarStats.totalRequests")}</span>
                <span className="text-white font-bold">{stats.total}</span>
              </button>
              
              {/* Noi */}
              <button
                onClick={() => handleCardClick("new")}
                className={cn(
                  "flex justify-between items-center w-full px-3 py-2 rounded-sm transition-all",
                  filter === "new" 
                    ? "bg-blue-500/20 ring-1 ring-blue-500" 
                    : "hover:bg-slate-800/50"
                )}
                data-testid="sidebar-filter-new"
              >
                <span className="text-blue-400 text-sm">{t("sidebarStats.new")}</span>
                <span className="text-blue-400 font-bold">{stats.new}</span>
              </button>
              
              {/* De Cotat / Cotate */}
              <button
                onClick={() => handleCardClick("quoted")}
                className={cn(
                  "flex justify-between items-center w-full px-3 py-2 rounded-sm transition-all",
                  filter === "quoted" 
                    ? "bg-yellow-500/20 ring-1 ring-yellow-500" 
                    : "hover:bg-slate-800/50"
                )}
                data-testid="sidebar-filter-quoted"
              >
                <span className="text-yellow-400 text-sm">{t("sidebarStats.quoted")}</span>
                <span className="text-yellow-400 font-bold">{stats.quoted}</span>
              </button>
              
              {/* Plătite */}
              <button
                onClick={() => handleCardClick("paid")}
                className={cn(
                  "flex justify-between items-center w-full px-3 py-2 rounded-sm transition-all",
                  filter === "paid" 
                    ? "bg-green-500/20 ring-1 ring-green-500" 
                    : "hover:bg-slate-800/50"
                )}
                data-testid="sidebar-filter-paid"
              >
                <span className="text-green-400 text-sm">{t("sidebarStats.paid")}</span>
                <span className="text-green-400 font-bold">{stats.paid}</span>
              </button>
              
              {/* Preluate */}
              <button
                onClick={() => handleCardClick("collected")}
                className={cn(
                  "flex justify-between items-center w-full px-3 py-2 rounded-sm transition-all",
                  filter === "collected" 
                    ? "bg-purple-500/20 ring-1 ring-purple-500" 
                    : "hover:bg-slate-800/50"
                )}
                data-testid="sidebar-filter-collected"
              >
                <span className="text-purple-400 text-sm">{t("sidebarStats.collected")}</span>
                <span className="text-purple-400 font-bold">{stats.collected}</span>
              </button>
              
              {/* Livrate */}
              <button
                onClick={() => handleCardClick("delivered")}
                className={cn(
                  "flex justify-between items-center w-full px-3 py-2 rounded-sm transition-all",
                  filter === "delivered" 
                    ? "bg-slate-500/20 ring-1 ring-slate-400" 
                    : "hover:bg-slate-800/50"
                )}
                data-testid="sidebar-filter-delivered"
              >
                <span className="text-slate-400 text-sm">{t("sidebarStats.delivered")}</span>
                <span className="text-slate-400 font-bold">{stats.delivered}</span>
              </button>
            </div>
          </div>
        )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 lg:mb-8 gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white">
              {viewMode === "invoices" ? t("headers.invoicesAdmin") : viewMode === "active" ? t("headers.requestsAdmin") : t("headers.archivedAdmin")} <span className="text-[#22c55e]">{t("headers.admin")}</span>
            </h1>
            <p className="text-slate-400">
              {viewMode === "invoices" ? t("headers.autoGeneratedInvoices") : viewMode === "active" ? t("headers.manageRequests") : t("headers.archivedRequests")}
            </p>
          </div>
          <Button 
            onClick={viewMode === "invoices" ? fetchInvoices : fetchData}
            variant="outline"
            className="border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", (viewMode === "invoices" ? loadingInvoices : loading) && "animate-spin")} />
            {t("headers.refresh")}
          </Button>
        </div>

        {/* Stats Cards - only in active view */}
        {stats && viewMode === "active" && (
          <>
            <div className="grid grid-cols-5 gap-4 mb-4">
              {[
                { label: t("statsCards.new"), value: stats.new, icon: FileText, color: "blue", status: "new" },
                { label: t("statsCards.quoted"), value: stats.quoted, icon: Clock, color: "yellow", status: "quoted" },
                { label: t("statsCards.paid"), value: stats.paid, icon: CreditCard, color: "green", status: "paid" },
                { label: t("statsCards.collected"), value: stats.collected, icon: Package, color: "purple", status: "collected" },
                { label: t("statsCards.delivered"), value: stats.delivered, icon: CheckCircle2, color: "slate", status: "delivered" },
              ].map((stat, index) => (
                <button 
                  key={index}
                  onClick={() => handleCardClick(stat.status)}
                  className={cn(
                    "bg-[#0f172a] border rounded-sm p-4 text-left transition-all duration-200 hover:scale-[1.02]",
                    filter === stat.status 
                      ? "border-[#22c55e] ring-2 ring-[#22c55e]/30 shadow-lg shadow-[#22c55e]/10" 
                      : "border-slate-800 hover:border-slate-600"
                  )}
                  data-testid={`filter-card-${stat.status}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <stat.icon className={cn(
                      "w-5 h-5",
                      stat.color === "blue" && "text-blue-400",
                      stat.color === "yellow" && "text-yellow-400",
                      stat.color === "green" && "text-green-400",
                      stat.color === "purple" && "text-purple-400",
                      stat.color === "slate" && "text-slate-400"
                    )} />
                    {filter === stat.status && (
                      <span className="text-[10px] uppercase tracking-wider text-[#22c55e] font-bold">
                        {t("statsCards.active")}
                      </span>
                    )}
                  </div>
                  <p className="font-['Barlow_Condensed'] text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-slate-500 text-sm">{stat.label}</p>
                </button>
              ))}
            </div>
            
            {/* Owner Stats Cards - Only visible to OWNER */}
            {isOwner && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border border-cyan-500/30 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                      <Eye className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="font-['Barlow_Condensed'] text-2xl font-bold text-cyan-400">{ownerStats.total_visits.toLocaleString()}</p>
                      <p className="text-slate-500 text-xs">{t("ownerStats.totalVisits")}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-violet-500/10 to-violet-500/5 border border-violet-500/30 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <p className="font-['Barlow_Condensed'] text-2xl font-bold text-violet-400">{ownerStats.unique_visitors.toLocaleString()}</p>
                      <p className="text-slate-500 text-xs">{t("ownerStats.uniqueVisitors")}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/30 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                      <Inbox className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="font-['Barlow_Condensed'] text-2xl font-bold text-orange-400">{ownerStats.requests_received.toLocaleString()}</p>
                      <p className="text-slate-500 text-xs">{t("ownerStats.requestsReceived")}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/30 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-['Barlow_Condensed'] text-2xl font-bold text-emerald-400">{ownerStats.paid_orders.toLocaleString()}</p>
                      <p className="text-slate-500 text-xs">{t("ownerStats.paidOrders")}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Total Paid GBP Banner */}
            {stats.total_paid_gbp !== undefined && (
              <div className="bg-gradient-to-r from-[#22c55e]/10 to-[#22c55e]/5 border border-[#22c55e]/30 rounded-sm p-4 mb-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#22c55e]/20 rounded-full flex items-center justify-center">
                    <PoundSterling className="w-6 h-6 text-[#22c55e]" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">{t("totalPaid.title")}</p>
                    <p className="font-['Barlow_Condensed'] text-2xl font-bold text-[#22c55e]">
                      {formatGBP(stats.total_paid_gbp)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-xs">{t("totalPaid.fromOrders")} {stats.paid} {t("totalPaid.paidOrders")}</p>
                </div>
              </div>
            )}
          </>
        )}

        {/* Bulk Action Bar */}
        {viewMode !== "invoices" && selectedIds.size > 0 && (
          <div className="bg-[#1e293b] border border-slate-700 rounded-sm p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-white font-medium">
                {selectedIds.size} {selectedIds.size > 1 ? t("bulkActions.selectedPlural") : t("bulkActions.selected")}
              </span>
              {viewMode === "active" && hasPaidInSelection && !isOwner && (
                <div className="flex items-center gap-2 text-amber-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{t("bulkActions.paidWillBeSkipped")}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {viewMode === "active" ? (
                <Button
                  onClick={() => setShowArchiveModal(true)}
                  disabled={eligibleForArchive.length === 0}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="bulk-archive-btn"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  {t("bulkActions.archiveSelected")} ({eligibleForArchive.length})
                </Button>
              ) : (
                <Button
                  onClick={() => setShowUnarchiveModal(true)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="bulk-unarchive-btn"
                >
                  <ArchiveRestore className="w-4 h-4 mr-2" />
                  {t("bulkActions.unarchiveSelected")} ({selectedIds.size})
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => setSelectedIds(new Set())}
                className="text-slate-400 hover:text-white"
                data-testid="bulk-cancel-selection-btn"
              >
                {t("bulkActions.cancel")}
              </Button>
            </div>
          </div>
        )}

        {/* Filters */}
        {viewMode !== "invoices" && (
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-4 mb-6" ref={tableRef}>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder={t("searchFilter.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-slate-900 border-slate-800 text-white"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px] bg-slate-900 border-slate-800 text-white" data-testid="status-filter">
                  <SelectValue placeholder={t("searchFilter.allStatuses")} />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="all" className="text-white">{t("searchFilter.all")}</SelectItem>
                  <SelectItem value="new" className="text-white">{t("statusLabels.new")}</SelectItem>
                  <SelectItem value="quoted" className="text-white">{t("statusLabels.quoted")}</SelectItem>
                  <SelectItem value="paid" className="text-white">{t("statusLabels.paid")}</SelectItem>
                  <SelectItem value="collected" className="text-white">{t("statusLabels.collected")}</SelectItem>
                  <SelectItem value="delivered" className="text-white">{t("statusLabels.delivered")}</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Clear filter button - shown when filter is active */}
              {filter !== "all" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilter}
                  className="text-slate-400 hover:text-white hover:bg-slate-800"
                  data-testid="clear-filter-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  {t("searchFilter.all")}
                </Button>
              )}
            </div>
          </div>
          
          {/* Active filter indicator */}
          {filter !== "all" && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-slate-500 text-sm">{t("searchFilter.activeFilter")}</span>
              <span className="px-2 py-1 bg-[#22c55e]/20 text-[#22c55e] rounded text-sm font-medium">
                {statusLabels[filter] || filter}
              </span>
              <span className="text-slate-500 text-sm">
                ({filteredQuotes.length} {filteredQuotes.length !== 1 ? t("searchFilter.resultsPlural") : t("searchFilter.results")})
              </span>
            </div>
          )}
        </div>
        )}

        {/* Table */}
        {viewMode !== "invoices" && (
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={toggleSelectAll}
                    className="border-slate-600"
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead className="text-slate-400 font-medium">{t("tableHeaders.company")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("tableHeaders.route")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("tableHeaders.cargo")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("tableHeaders.urgency")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("tableHeaders.status")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("tableHeaders.price")}</TableHead>
                <TableHead className="text-slate-400 font-medium text-right">{t("tableHeaders.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    {t("tableContent.loading")}
                  </TableCell>
                </TableRow>
              ) : filteredQuotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    {viewMode === "archived" ? t("tableContent.noArchivedRequests") : t("tableContent.noRequests")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredQuotes.map((quote) => (
                  <TableRow 
                    key={quote.id} 
                    className={cn(
                      "border-slate-800 hover:bg-slate-800/30",
                      selectedIds.has(quote.id) && "bg-slate-800/50"
                    )}
                    data-testid={`quote-row-${quote.id}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(quote.id)}
                        onCheckedChange={() => toggleSelectOne(quote.id)}
                        className="border-slate-600"
                        data-testid={`select-checkbox-${quote.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{quote.company_name}</p>
                        <p className="text-slate-500 text-xs">{quote.contact_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-3">
                        <div data-testid={`admin-list-pickup-address-${quote.id}`}>
                          <div className="text-[10px] uppercase tracking-wide text-slate-500">{t("tableContent.pickup")}</div>
                          {buildAddressLines(quote.pickup_address, quote.pickup_postcode).map((line, index) => (
                            <div key={`pickup-${quote.id}-${index}`} className="text-xs text-slate-200">
                              {line}
                            </div>
                          ))}
                        </div>
                        <div data-testid={`admin-list-delivery-address-${quote.id}`}>
                          <div className="text-[10px] uppercase tracking-wide text-slate-500">{t("tableContent.delivery")}</div>
                          {buildAddressLines(quote.delivery_address, quote.delivery_postcode).map((line, index) => (
                            <div key={`delivery-${quote.id}-${index}`} className="text-xs text-slate-200">
                              {line}
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-300 capitalize text-sm">
                        {quote.cargo_type}
                        {quote.pallet_count && ` (${quote.pallet_count})`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        "text-xs capitalize",
                        quote.urgency === "immediate" && "text-red-400",
                        quote.urgency === "same_day" && "text-yellow-400",
                        quote.urgency === "overnight" && "text-blue-400"
                      )}>
                        {quote.urgency.replace('_', ' ')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        "px-2 py-1 rounded text-xs font-medium",
                        statusColors[quote.status]
                      )}>
                        {statusLabels[quote.status]}
                      </span>
                    </TableCell>
                    <TableCell>
                      {quote.quoted_price ? (
                        <span className="text-[#22c55e] font-bold">
                          £{quote.quoted_price.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={`/admin/request/${quote.id}`}
                        className="inline-flex"
                        data-testid={`view-quote-link-${quote.id}`}
                      >
                        <Button 
                          size="sm" 
                          className="bg-[#3b82f6] hover:bg-[#3b82f6]/90 text-white"
                          data-testid={`view-quote-btn-${quote.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {t("tableContent.view")}
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        )}

        {viewMode === "invoices" && (
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm overflow-hidden" data-testid="invoices-table">
          {/* Link to Bulk Invoice Management - OWNER ONLY */}
          {isOwner && (
            <div className="p-4 border-b border-slate-800 bg-slate-800/30">
              <Link
                to="/admin/bulk-invoices"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#22c55e] hover:bg-[#22c55e]/90 text-[#020617] rounded font-medium transition-colors"
                data-testid="bulk-invoices-link"
              >
                <FileText className="w-4 h-4" />
                {t("bulkInvoices.bulkActions")}
              </Link>
              <span className="text-slate-400 text-sm ml-3">
                {language === 'ro' ? 'Gestionare facturi în bloc, filtrare după perioadă, rezumat' : 'Bulk invoice management, date filtering, summary reports'}
              </span>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400 font-medium">{t("invoicesTable.invoice")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("invoicesTable.client")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("invoicesTable.email")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("invoicesTable.paidAt")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("invoicesTable.period")}</TableHead>
                <TableHead className="text-slate-400 font-medium">{t("invoicesTable.amount")}</TableHead>
                <TableHead className="text-slate-400 font-medium text-right">{t("tableHeaders.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingInvoices ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    {t("invoicesTable.loadingInvoices")}
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    {t("invoicesTable.noInvoices")}
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id} className="border-slate-800 hover:bg-slate-800/30">
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{invoice.invoice_number}</p>
                        <p className="text-slate-500 text-xs">#{invoice.quote_id?.slice(0, 8)}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{invoice.company_name || "—"}</p>
                        <p className="text-slate-500 text-xs">{invoice.contact_name || "—"}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-300 text-sm">{invoice.email || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-300 text-sm">{formatPaidDate(invoice.paid_at)}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-300">
                        <p>{invoice.paid_week} / {invoice.paid_month} / {invoice.paid_year}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-[#22c55e] font-bold">
                        {formatGBP(invoice.amount_total)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-700 text-slate-200 hover:bg-slate-800"
                          onClick={async () => {
                            try {
                              await openInvoicePdf(invoice.id);
                            } catch (error) {
                              console.error('PDF error:', error);
                              toast.error(t("toasts.invoicePrintError"));
                            }
                          }}
                          data-testid={`invoice-pdf-btn-${invoice.id}`}
                        >
                          <FileDown className="w-4 h-4 mr-1" />
                          {t("invoicesTable.pdf")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-700 text-slate-200 hover:bg-slate-800"
                          onClick={() => handlePrintInvoice(invoice.id)}
                          data-testid={`invoice-print-btn-${invoice.id}`}
                        >
                          <Printer className="w-4 h-4 mr-1" />
                          {t("invoicesTable.print")}
                        </Button>
                        <Button
                          size="sm"
                          className="bg-[#3b82f6] hover:bg-[#3b82f6]/90 text-white"
                          onClick={() => handleShareInvoice(invoice)}
                          disabled={sharing}
                          data-testid={`invoice-share-btn-${invoice.id}`}
                        >
                          <Share2 className="w-4 h-4 mr-1" />
                          {t("invoicesTable.share")}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        )}
      </main>

      {/* Invoice Share Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="bg-[#1e293b] border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-400" />
              {t("shareModal.title")}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {shareInvoice?.invoice_number} — {shareInvoice?.company_name || "Client"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button
              onClick={handleCopyShareLink}
              className="w-full bg-slate-800 hover:bg-slate-700 text-white"
              data-testid="invoice-copy-link-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t("shareModal.copyLink")}
            </Button>
            <Button
              onClick={async () => {
                if (shareInvoice) {
                  try {
                    await openInvoicePdf(shareInvoice.id);
                  } catch (error) {
                    console.error('PDF error:', error);
                    toast.error(t("toasts.invoicePrintError"));
                  }
                }
              }}
              variant="outline"
              className="w-full border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="invoice-download-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {t("shareModal.downloadPdf")}
            </Button>
            <Button
              onClick={handleSendInvoiceEmail}
              disabled={sendingInvoice}
              className="w-full bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90"
              data-testid="invoice-send-email-btn"
            >
              {sendingInvoice ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("shareModal.sending")}
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  {t("shareModal.sendEmail")}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Modal */}
      <Dialog open={showArchiveModal} onOpenChange={setShowArchiveModal}>
        <DialogContent className="bg-[#1e293b] border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-400" />
              {t("archiveModal.title")}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {eligibleForArchive.length} {t("archiveModal.requestsWillBeArchived")}
              {hasPaidInSelection && !isOwner && (
                <span className="block mt-2 text-amber-400">
                  ⚠️ {selectedIds.size - eligibleForArchive.length} {t("archiveModal.paidRequestsSkipped")}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <label className="text-sm text-slate-400 mb-2 block">
              {t("archiveModal.archiveReason")}
            </label>
            <Textarea
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder={t("archiveModal.archiveReasonPlaceholder")}
              className="bg-slate-900 border-slate-700 text-white"
            />
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowArchiveModal(false)}
              className="text-slate-400"
              data-testid="archive-cancel-btn"
            >
              {t("bulkActions.cancel")}
            </Button>
            <Button
              onClick={handleBulkArchive}
              disabled={bulkLoading || eligibleForArchive.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="archive-confirm-btn"
            >
              {bulkLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("archiveModal.archiving")}
                </>
              ) : (
                <>
                  <Archive className="w-4 h-4 mr-2" />
                  {t("archiveModal.archive")} {eligibleForArchive.length}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unarchive Confirmation Modal */}
      <Dialog open={showUnarchiveModal} onOpenChange={setShowUnarchiveModal}>
        <DialogContent className="bg-[#1e293b] border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArchiveRestore className="w-5 h-5 text-green-400" />
              {t("unarchiveModal.title")}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedIds.size} {t("unarchiveModal.requestsWillBeUnarchived")}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowUnarchiveModal(false)}
              className="text-slate-400"
              data-testid="unarchive-cancel-btn"
            >
              {t("bulkActions.cancel")}
            </Button>
            <Button
              onClick={handleBulkUnarchive}
              disabled={bulkLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="unarchive-confirm-btn"
            >
              {bulkLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("unarchiveModal.unarchiving")}
                </>
              ) : (
                <>
                  <ArchiveRestore className="w-4 h-4 mr-2" />
                  {t("unarchiveModal.unarchive")} {selectedIds.size}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
